import { type FC } from "react";

import { KEYBOARD_SHORTCUT_CATEGORIES, type KeyboardShortcutKey } from "./shortcuts";

import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import {
  CommandIcon,
  HandRaiseCircleIcon,
  ReactionAgreeIcon,
  ReactionClapIcon,
  ReactionDisagreeIcon,
  ReactionDislikeIcon,
  ReactionLaughIcon,
  ReactionLikeIcon,
  ReactionLoveIcon,
  ReactionVoteNoIcon,
  ReactionVoteYesIcon,
  ReactionWowIcon,
  ShiftIcon,
} from "mds/icons";
import type { IconProps } from "mds/utils/images";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { viewStateActions } from "store/slices/view";
import { filterMap } from "utils/collections";
import { isMacOS, modKey } from "utils/keyboard";

const LEFT_COLUMN_CATEGORIES = ["general", "audio", "page_navigation", "assessment_mode"] as const;
const RIGHT_COLUMN_CATEGORIES = ["reactions", "participation"] as const;

const SHORTCUT_ICONS: Partial<Record<KeyboardShortcutKey, FC<IconProps>>> = {
  like: ReactionLikeIcon,
  dislike: ReactionDislikeIcon,
  agree: ReactionAgreeIcon,
  disagree: ReactionDisagreeIcon,
  laugh: ReactionLaughIcon,
  wow: ReactionWowIcon,
  love: ReactionLoveIcon,
  clap: ReactionClapIcon,
  yes: ReactionVoteYesIcon,
  no: ReactionVoteNoIcon,
  hand_raise: HandRaiseCircleIcon,
};

const renderShortcutIcon = (label: KeyboardShortcutKey) => {
  const Icon = SHORTCUT_ICONS[label];
  return Icon ? (
    <Icon className={label === "hand_raise" ? "text-blue" : undefined} size="xl" />
  ) : null;
};

const renderKey = (key: string) => (
  <kbd className="flex h-8 min-w-[34px] items-center justify-center rounded-lg bg-black-tint-97 px-3 font-sans text-sm font-normal text-black-tint-20">
    {key === "Mod" ? (
      isMacOS ? (
        <CommandIcon size="m" />
      ) : (
        modKey
      )
    ) : key === "Shift" ? (
      <ShiftIcon size="m" />
    ) : (
      key
    )}
  </kbd>
);

export const KeyboardShortcutsDialog: FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.view.isKeyboardShortcutsDialogOpen);
  const { canAuthorSection: isInstructor } = useCapabilities();

  const close = () => dispatch(viewStateActions.setKeyboardShortcutsDialogOpen(false));

  const renderCategory = (
    categoryKey: (typeof LEFT_COLUMN_CATEGORIES)[number] | (typeof RIGHT_COLUMN_CATEGORIES)[number],
  ) => {
    const category = KEYBOARD_SHORTCUT_CATEGORIES.find(({ title }) => title === categoryKey);
    if (
      !category ||
      (category.shortcuts.every((shortcut) => shortcut.instructorOnly) && !isInstructor)
    ) {
      return null;
    }

    return (
      <section className="flex flex-col gap-3" key={category.title}>
        <h2 className="h4 flex border-b border-black-tint-90 pb-3 text-black-tint-20">
          {t(`keyboard_shortcuts.categories.${category.title}`)}
        </h2>
        <div className="flex flex-col gap-3">
          {filterMap(
            category.shortcuts,
            (shortcut) => !shortcut.instructorOnly || isInstructor,
            (shortcut) => (
              <div className="flex items-center justify-between gap-3" key={shortcut.label}>
                <div className="flex min-w-0 items-center gap-2">
                  {renderShortcutIcon(shortcut.label)}
                  <span className="body-s text-black-tint-40">
                    {t(`keyboard_shortcuts.shortcuts.${shortcut.label}`)}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {shortcut.keys.map((key, index) => (
                    <>
                      {index > 0 && <span className="px-0.5 text-sm text-black-tint-20">+</span>}
                      {renderKey(key)}
                    </>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </section>
    );
  };

  return (
    <Dialog
      className="z-[200]"
      dialogContainerClassName="box-border mx-[5vw] max-h-[90vh] w-full max-w-[800px] pt-8 md:px-10 md:pb-10"
      dialogPanelClassName="w-full"
      open={isOpen}
      title={t("keyboard_shortcuts.title")}
      titleClassName="h2 mb-6 text-black-tint-20"
      onClose={close}
    >
      <div className="grid gap-x-10 gap-y-8 md:grid-cols-2">
        <div className="flex flex-col gap-8">{LEFT_COLUMN_CATEGORIES.map(renderCategory)}</div>
        <div className="flex flex-col gap-8">
          {RIGHT_COLUMN_CATEGORIES.map((categoryKey) => renderCategory(categoryKey))}
        </div>
      </div>
    </Dialog>
  );
};
